/**
 * A segment's stored condition, read as rows and written back from them.
 *
 * A segment is a named condition and nothing else: no action, no source, just
 * `cases` in the shape the reader hands to json-logic,
 * `{ or: [ { and: [ ...operations... ] } ] }`. This module is the only place
 * that takes that apart and puts it back together, so the list that reads a
 * segment out loud and the editor that changes it can never disagree about
 * what is stored.
 *
 * THE ONE RULE: what goes in comes out. `casesFromRows(rowsFromCases(x))` has
 * to give `x` back for every shape a customer's theme_settings can hold. This
 * codebase has lost a working condition to a broken round trip more than once
 * (mapGroupPaths here, entryIsReadable in editor-v2/utils/conditionModel.ts),
 * so fidelity is kept at three levels:
 *
 *   1. an operation this module cannot read travels verbatim on `raw`;
 *   2. an operation it CAN read is written back as the very object it was read
 *      from, as long as it still says the same thing (keepUnchangedOperation),
 *      so a stored '!!' from before the number 0 counted as answered is not
 *      migrated by nothing more than being looked at;
 *   3. an OR-alternative that is a BARE operation rather than an AND-list goes
 *      back bare. The reader accepts both, and rewriting one into the other
 *      would be a change to stored customer data with nothing gained.
 *
 * Two things deliberately do NOT come back out the way they went in, both
 * documented where they happen:
 *   - an address-bar parameter stored without its namespace gains it, because
 *     without it the condition has never been able to match (see
 *     addUrlParameterPrefix);
 *   - an alternative with an EMPTY and-list falls away (see casesFromRows).
 *     `{ and: [] }` is an alternative that can never be true, so dropping it
 *     leaves the segment matching exactly the same visitors.
 *
 * The json-logic itself is never assembled here: `buildOperation` and
 * `parseOperation` own that, including the reversed argument order of `in` and
 * the value-less shape of '!!' / '!'.
 *
 * A row is `{ id, path, operator, value?, kind, fieldType, segmentId?, raw?,
 * original?, bareAlternative? }` — the same shape conditionRows.js works on
 * and conditionSentence.js reads, plus `bareAlternative` (see point 3 above).
 *
 * Plain JavaScript, no Vue, no dependency beyond the shared modules next door.
 */
import { buildOperation, keepUnchangedOperation, parseOperation } from './contextRuleHelpers';

// The namespace the reader's data object puts the query string under
// (buildData in files/_app_dev/js/conditions.js). A segment's `cases` are
// always evaluated against that object — never through contextApply's
// top-level query-string branch — so an address-bar parameter only resolves
// when it is stored under this namespace. It is storage, not a name: the
// editor sees and types the plain parameter name.
export const URL_PARAMETER_PREFIX = 'urlParameter.';

/**
 * The stored form of an address-bar path.
 *
 * This is the ONE place where reading and writing do not cancel out. A segment
 * built before the namespace existed stored the bare parameter name, and that
 * name has never been in the reader's data object: the segment could not
 * match, whatever URL the visitor arrived with. Writing such a condition back
 * puts the namespace on and the segment starts working — the same repair
 * `mergeContextCases` in ProjectSettingsSegments.vue has been doing since the
 * namespace was introduced. A name that already carries it is left alone, so
 * the prefix is never stacked twice.
 */
export function addUrlParameterPrefix(path) {
  return path.startsWith(URL_PARAMETER_PREFIX) ? path : `${URL_PARAMETER_PREFIX}${path}`;
}

export function stripUrlParameterPrefix(path) {
  return path.startsWith(URL_PARAMETER_PREFIX) ? path.slice(URL_PARAMETER_PREFIX.length) : path;
}

/**
 * Which source a stored path belongs to. Derived from the path exactly as
 * ModalCreateContextItem.vue builds it — TWO personalization shapes, both
 * checked explicitly rather than inferred, since guessing from a generic
 * pattern ("the first segment is numeric") misses the second one:
 *   - a normal field:           "<numeric setId>.<fieldId>", e.g. "7.tevis"
 *   - "use the set as context": the fixed literal "personalized_variable_set_id"
 *     (never prefixed, never suffixed, never anything else)
 * A form path always starts with 'form.'; anything matching neither
 * personalization shape nor the form prefix is a plain address-bar name.
 *
 * Asked of the STORED path, before the namespace comes off: a parameter that
 * happens to be named like a number would look like a personalization field
 * once "urlParameter." is gone.
 */
export function kindForPath(path) {
  if (!path) return 'urlParameter';
  if (path.startsWith(URL_PARAMETER_PREFIX)) return 'urlParameter';
  if (path.startsWith('form.')) return 'form';
  if (path === 'personalized_variable_set_id') return 'personalization';
  const [first] = path.split('.');
  return /^\d+$/.test(first) ? 'personalization' : 'urlParameter';
}

// A row id is a v-for key and nothing else — it is never stored, and
// casesFromRows never looks at it. The prefix keeps it apart from the `row-N`
// ids conditionModel.ts hands out and the `panel-N` ones conditionRows.js
// adds, so a panel that shows rows from more than one of them cannot reuse
// one row's dropdowns for another.
let rowSeq = 0;
function nextId() {
  rowSeq += 1;
  return `seg-${rowSeq}`;
}

/**
 * A reference to another segment, as it stands INSIDE a rule tree:
 * { segment: '<id>' }, negated { '!': { segment: '<id>' } } — the shape
 * editor-v2 writes (conditionModel.ts) and conditions.js registers as a
 * json-logic operation. parseOperation knows neither ('!' only reads a `var`),
 * so without this a segment reference would read as "von Hand geschrieben".
 *
 * '==' / '!=' are what the sentence builder and the comparison picker read a
 * segment row's "erfüllt" and "nicht erfüllt" from.
 */
function parseSegmentOperation(op) {
  if (!op || typeof op !== 'object' || Array.isArray(op)) return null;
  const keys = Object.keys(op);
  if (keys.length !== 1) return null;
  const negated = keys[0] === '!' && op['!'] && typeof op['!'] === 'object' && !Array.isArray(op['!'])
    ? op['!']
    : null;
  const inner = negated || op;
  if (Object.keys(inner).length !== 1 || typeof inner.segment !== 'string' || !inner.segment) {
    return null;
  }
  return { segmentId: inner.segment, operator: negated ? '!=' : '==' };
}

function rawRow(operation) {
  return {
    id: nextId(), path: '', operator: '', kind: 'urlParameter', fieldType: 'text', raw: operation,
  };
}

/**
 * One stored json-logic operation as a row. An operation neither
 * parseSegmentOperation nor parseOperation recognizes becomes a `raw` row,
 * which the sentence names as hand-written and this module writes back byte
 * for byte instead of dropping it.
 *
 * @param {*} operation
 * @param {Function} [fieldTypeForPath]  Given the STORED path, the field's
 *        type ('number', 'radio', ...) or undefined when there is no field
 *        list to ask. Only the sentence and the value picker use it; what
 *        gets WRITTEN never depends on it for an untouched row.
 */
function rowForOperation(operation, fieldTypeForPath) {
  const segmentReference = parseSegmentOperation(operation);
  if (segmentReference) {
    return {
      id: nextId(),
      path: segmentReference.segmentId,
      operator: segmentReference.operator,
      kind: 'segment',
      fieldType: 'segment',
      segmentId: segmentReference.segmentId,
    };
  }

  // A `var` is a string only because every rule this codebase has ever written
  // put one there; a hand-edited or imported theme_settings can hold
  // { "var": 7 }. Reading it as a path threw "path.startsWith is not a
  // function", and because the sentence is built during RENDER, Vue aborted
  // the render of the WHOLE panel — the healthy segments next to the broken
  // one included. Such an operation travels as `raw` instead.
  const parsed = parseOperation(operation);
  if (!parsed || typeof parsed.path !== 'string') return rawRow(operation);

  const kind = kindForPath(parsed.path);
  const type = typeof fieldTypeForPath === 'function' ? fieldTypeForPath(parsed.path) : undefined;
  return {
    id: nextId(),
    // The namespace is storage, not a name — see URL_PARAMETER_PREFIX.
    path: kind === 'urlParameter' ? stripUrlParameterPrefix(parsed.path) : parsed.path,
    operator: parsed.operator,
    value: parsed.value,
    kind,
    // Without the real type the value is written out wrongly: 10000 in a
    // number field reads "10.000", the same digits in a text field must stay
    // "10000". 'text' is the answer that changes nothing when no field list
    // was given.
    fieldType: type || 'text',
    // The operation this row was READ from, so a row nobody touched can be
    // written back exactly as it was found — see rule 2 in the file header.
    original: operation,
  };
}

/**
 * One OR-alternative as a list of rows.
 *
 * An alternative is normally an { and: [...] } list, but it may also be a BARE
 * operation: `normalizeCases` in ProjectSettingsSegments.vue produces exactly
 * that when it wraps a hand-written or imported rule as { or: [rule] }, and a
 * stored `cases.or` is passed through unexamined. Reading only `group.and`
 * reported such a segment as empty, and writing it back as { and: [...] }
 * would rewrite stored customer data for nothing — so the bare shape is
 * remembered on the row and restored on write.
 */
function rowsOfGroup(group, fieldTypeForPath) {
  if (!group || typeof group !== 'object') {
    // Not something that can hold operations at all. Kept whole rather than
    // dropped: an alternative this module cannot read is still an alternative
    // the reader evaluates. (`undefined` cannot come out of stored JSON and
    // has nothing to keep.)
    return group === undefined ? [] : [{ ...rawRow(group), bareAlternative: true }];
  }
  if (Array.isArray(group.and)) {
    return group.and.map((operation) => rowForOperation(operation, fieldTypeForPath));
  }
  return [{ ...rowForOperation(group, fieldTypeForPath), bareAlternative: true }];
}

/**
 * A segment's stored condition as OR-groups of AND-rows.
 *
 * Reads `cases.or` and nothing else. A `cases` in any other shape — the old
 * array, a bare rule, a missing field — is the caller's to heal first
 * (`normalizeCases` in ProjectSettingsSegments.vue does it on load, and has
 * its own tests); guessing here would mean two places that repair stored
 * shapes and a round trip that no longer closes.
 *
 * @param {*} cases
 * @param {Function} [fieldTypeForPath]  See rowForOperation.
 * @returns {Array} OR-groups, each an array of rows.
 */
export function rowsFromCases(cases, fieldTypeForPath) {
  const or = cases && typeof cases === 'object' && Array.isArray(cases.or) ? cases.or : [];
  return or.map((group) => rowsOfGroup(group, fieldTypeForPath));
}

function operationFromRow(row) {
  if (row.raw !== undefined) return row.raw;

  if (row.kind === 'segment') {
    // Inside a rule tree a segment is an operation, not a whole rule.
    // conditions.js registers `segment` as a json-logic operation, so this
    // resolves the same way a segment-sourced element rule does.
    const op = { segment: row.segmentId };
    return row.operator === '!=' ? { '!': op } : op;
  }

  // Rebuilt, then handed back to the operation this row was read from when the
  // two say the same thing. A row the editor really changed no longer says the
  // same thing and is written new; a row nobody touched keeps its stored
  // shape, '!!' included.
  //
  // `row.value` goes along as the third argument, so "unchanged" is judged
  // against what the row holds and not against the rebuilt literal. Without it
  // a stored "true" on a yes/no field and a stored "10000" on a number field
  // were rewritten into `true` and `10000` by nothing but their field's type
  // -- and for json-logic that is a change of meaning ('true' == true is
  // false). See keepUnchangedOperation.
  return keepUnchangedOperation(row.original, buildOperation({
    path: row.kind === 'urlParameter' ? addUrlParameterPrefix(row.path) : row.path,
    operator: row.operator,
    value: row.value,
    fieldType: row.fieldType,
  }), row.value);
}

/**
 * The rows back as a segment's `cases`.
 *
 * An empty group falls away: a visible "oder" with nothing behind it would
 * disappear on the next save anyway, and `{ and: [] }` is an alternative that
 * can never be true.
 *
 * @param {Array} groups  OR-groups, each an array of rows.
 * @returns {object} `{ or: [...] }` — always that shape, never an array and
 *          never a bare rule, because that is the only shape the reader
 *          evaluates as a condition rather than as "matches everything".
 */
export function casesFromRows(groups) {
  const or = [];
  (groups || []).forEach((group) => {
    if (!Array.isArray(group) || group.length === 0) return;
    const operations = group.map(operationFromRow);
    // A single row that was read from a bare alternative goes back bare; the
    // moment it has company it needs the { and: [...] } list to hold them.
    if (operations.length === 1 && group[0].bareAlternative) {
      or.push(operations[0]);
      return;
    }
    or.push({ and: operations });
  });
  return { or };
}
